import _ from "lodash";
import React from "react";

// Whole units: volumes run into the millions, and cents only made the
// columns ragged (582,737.15 next to 18,862,880.4).
let numberFormat = new Intl.NumberFormat('en-us', {
    style: 'decimal',
    maximumFractionDigits: 0,
});

// platform_overview amounts are in each loan tape's own currency (no EUR
// conversion), so an amount always carries its currency code.
export const formatCurrency = (value, currency) => {
    if (value == undefined) {
        return '-';
    }
    return currency ? `${numberFormat.format(value)} ${currency}` : `${numberFormat.format(value)}`;
}

export const formatCount = (value) => (value == undefined ? '-' : numberFormat.format(value));

// EUR first (almost every platform), the rest alphabetically - ArangoDB
// doesn't keep by_currency's key order.
export const sortCurrencies = (currencies) => [...currencies].sort((a, b) => {
    if (a === b) return 0;
    if (a === 'EUR') return -1;
    if (b === 'EUR') return 1;
    return String(a).localeCompare(String(b));
});

// [{ currency, value }] for one field of a GET /overview row: one entry per
// currency the platform's loans are in (per_currency), never summed across
// currencies. Entries without a value are left out.
export const currencyValues = (row, field) => {
    const entries = (row?.per_currency || []).filter((p) => p[field] != null);
    return sortCurrencies(entries.map((p) => p.currency)).map((currency) => ({
        currency,
        value: entries.find((p) => p.currency === currency)[field],
    }));
}

// Sums [{ currency, value }] lists per currency - amounts in different
// currencies are never added together.
export const sumByCurrency = (amountLists) => {
    const sums = {};
    for (const amounts of amountLists) {
        for (const { currency, value } of amounts) {
            const key = currency ?? '';
            sums[key] = (sums[key] || 0) + value;
        }
    }
    return sortCurrencies(Object.keys(sums)).map((c) => ({ currency: c || undefined, value: sums[c] }));
}

// One line per currency. Amounts carry their own currency code; other
// figures (rates, terms) only get a currency prefix when there are several.
export const renderPerCurrency = (entries, format = null) => {
    if (!entries.length) return '-';
    const line = ({ currency, value }) => (format
        ? (entries.length > 1 ? `${currency}: ${format(value)}` : format(value))
        : formatCurrency(value, currency));
    if (entries.length === 1) return line(entries[0]);
    return <div className="ex-per-currency">{entries.map((e) => <div key={e.currency}>{line(e)}</div>)}</div>;
}

export const formatPercentage = (value) => {
    if (value == undefined) {
        return '-';
    }
    return `${(value * 100).toFixed(2)}%`;
}

export const formatTerm = (value) => {
    if (value == undefined) {
        return '-';
    }
    return `${_.ceil(value * 2) / 2}m`;
}

const formatValue = (value) => {
    if (value == undefined || value == null) {
        return '-';
    }
    return `${value}`;
}

const PLATFORM_COLUMN = { header: 'Originator', field: 'platform' };
const LOAN_COUNT_COLUMN = {
    header: 'Loan Count',
    field: 'total_loan_count',
    numeric: true,
    body: rowData => formatCount(rowData.total_loan_count),
};

const amountColumn = (header, field) => ({
    header,
    field: `total_${field}`,
    numeric: true,
    body: rowData => renderPerCurrency(currencyValues(rowData, field)),
});

const metricColumn = (header, field, format) => ({
    header,
    field: `per_currency.0.${field}`,
    numeric: true,
    body: rowData => renderPerCurrency(currencyValues(rowData, field), format),
});

// Each platform's overview covers all its loans - there is no SME/CONSUMER
// split. The table has two views (picked in platformOverview.js) so neither
// needs more columns than the card has room for.
export const PLATFORM_OVERVIEW_VOLUME_COLUMNS = [
    PLATFORM_COLUMN,
    LOAN_COUNT_COLUMN,
    amountColumn('Origination Volume Since 3 Months', 'origination_volume_since_3_months'),
    amountColumn('Origination Volume Since 6 Months', 'origination_volume_since_6_months'),
    amountColumn('Origination Volume Since 12 Months', 'origination_volume_since_12_months'),
    amountColumn('Origination Volume Since Inception', 'origination_volume'),
    { header: 'Currency', field: 'original_currency' },
];

// Averages and rates. The default rate is a share of loans, so it exists
// for the platform as a whole; the averages exist per currency.
export const PLATFORM_OVERVIEW_METRIC_COLUMNS = [
    PLATFORM_COLUMN,
    LOAN_COUNT_COLUMN,
    {
        header: 'Default Rate',
        field: 'default_rate_by_platform_status',
        numeric: true,
        body: rowData => formatPercentage(rowData.default_rate_by_platform_status),
    },
    metricColumn('Average Term', 'average_term', formatTerm),
    metricColumn('Average Interest Rate', 'average_interest_rate', formatPercentage),
    amountColumn('Average Loan Size', 'average_loan_size'),
    metricColumn('Recovery Rate', 'recovery_rate', formatPercentage),
    {
        header: 'Last Updated',
        field: 'last_updated',
        body: rowData => formatValue(rowData.last_updated),
    },
];
